import React, { useEffect, useState } from "react";
import Loader from "../components/common/loader/loader";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import Error from "../components/common/error/error";
import {
  createCategory,
  getCategoryById,
  updateCategory,
} from "../api/admin/categories";
import { notify } from "../lib/utils";
import CreateUpdateCategoryForm from "../components/categories/create-update-category-form";
import { listSubcategoriesForForms } from "../api/admin/subcategories";
import usePageTitle from "../hooks/usePageTitle";

function CategoryFormPage() {
  const params = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState<any>(null);

  const [subcategories, setSubcategories] = useState<any>([]);

  const [isEditMode, setIsEditMode] = useState(false);

  usePageTitle(
    isEditMode ? `Edit Category: ${category?.internalName}` : "Create Category"
  );

  const [loading, setLoading] = React.useState(true);

  const [btnLoading, setBtnLoading] = React.useState(false); // when saving

  const [error, setError] = React.useState<any>(false);

  const loadCategoryById = async (id: string) => {
    // fetch the template by id
    const response = await getCategoryById(id);

    if (!response || response.status !== 200) {
      setError("Error loading Category");
      setLoading(false);
      setIsEditMode(false);
      return;
    }

    setIsEditMode(true);
    setCategory(response.data);
    setError(null);
    setLoading(false);
  };

  const loadSubcategoriesForForm = async () => {
    const response = await listSubcategoriesForForms();

    if (!response || response.status !== 200) {
      setError("Error loading Subcategories list");
      return;
    }

    setSubcategories(response.data);
  };

  useEffect(() => {
    loadSubcategoriesForForm();

    if (params.id) {
      loadCategoryById(params.id);
    } else {
      setIsEditMode(false);
      setLoading(false);
      setError(false);
    }
    // reset the state when unmounting

    return () => {
      setCategory(null);
      setLoading(false);
      setError(false);
      setIsEditMode(false);
      setBtnLoading(false);
      setSubcategories([]);
    };
  }, []);

  // handle category creation
  const handleCreateCategory = async ({
    categoryData,
    selectedSubcategories,
  }: any) => {
    // merge them in object.
    const data = {
      categoryData,
      selectedSubcategories,
    };

    const response = await createCategory(data);

    if (!response || response.status !== 201) {
      // check slug duplication
      if (response?.status === 409) {
        notify({
          type: "error",
          message: "Category already exists",
        });
      } else {
        notify({
          type: "error",
          message: "Error creating category",
        });
      }

      return;
    }

    notify({
      type: "success",
      message: "Category created successfully",
    });

    // redirect to the category list page
    navigate(ROUTES.CATEGORIES);
  };

  // handle category creation
  const handleUpdateCategory = async ({
    categoryId,
    categoryData,
    selectedSubcategories,
    removedSubcategories,
  }: any) => {
    // merge them in object.
    const data = {
      categoryData,
      selectedSubcategories,
      removedSubcategories,
    };

    const response = await updateCategory(categoryId, data);

    if (!response || response.status !== 200) {
      // check slug duplication
      if (response?.status === 409) {
        notify({
          type: "error",
          message: "Category Slug already exists",
        });
      } else {
        notify({
          type: "error",
          message: "Error updating category",
        });
      }

      return;
    }

    notify({
      type: "success",
      message: "Category updated successfully",
    });

    // redirect to the category list page
    navigate(ROUTES.CATEGORIES);
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error message={error} />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">
              {isEditMode
                ? `Edit Category: ${category?.internalName}`
                : "Create Category"}
            </h1>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <CreateUpdateCategoryForm
              category={category}
              btnLoading={btnLoading}
              isEditMode={isEditMode}
              subcategories={subcategories}
              onFormSubmission={(obj: any) => {
                if (isEditMode) {
                  handleUpdateCategory(obj);
                } else {
                  handleCreateCategory(obj);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryFormPage;
